import {
  Box,
  Typography,
  Divider,
  Avatar,
  makeStyles,
  createStyles,
  Theme,
} from "@material-ui/core";
import { GlobalModal } from "components/globalModal/GlobalModal";
import { TableCustom } from "components/table/TableCustom";
import { SearchByNameOrId } from "components/userManagement/SearchByNameOrId";
import { EmployeeModel } from "@dwo/shared/dist/models/employeeModel";
import React, { useCallback, useEffect, useState } from "react";
import { ServiceOptions } from "@dwo/shared/dist/services/baseService";
import {
  getForemanAssignmentRows,
  supervisorAssignmentColumns,
} from "utils/employeeDetailsUtils";
import { useDispatch, useSelector } from "react-redux";
import {
  selectEmployeesReqStatus,
  selectEmployees,
  setEmployeesReqStatus,
  getAllEmployeesWithImage,
} from "features/employees/employeesSlice";
import { ReqStatus } from "utils/sharedUtils";
import { noop } from "lodash/fp";

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    textAssigned: {
      color: theme.palette.grey["500"],
    },
  }),
);

interface ForemanModalProps {
  isOpen: boolean;
  foremanAssigned?: EmployeeModel;
  regionId?: number;
  onClickCancel: () => void;
  onClickSave: (foremanSelectedId: string) => void;
}

export function ForemanModal({
  isOpen = false,
  foremanAssigned,
  regionId,
  onClickCancel,
  onClickSave,
}: ForemanModalProps) {
  const classes = useStyles();
  const dispatch = useDispatch();
  const employeesReqStatus = useSelector(selectEmployeesReqStatus);
  const foremenList = useSelector(selectEmployees).filter(
    (foreman) => foreman.id !== foremanAssigned?.id,
  );
  const isLoading = employeesReqStatus !== ReqStatus.FULFILLED;
  const [foremanIdSelected, setForemanIdSelected] = useState<string>("");
  const [searchedName, setSearchedName] = useState("");
  const [searchedEmployeeId, setSearchedEmployeeId] = useState("");
  const [query] = useState<ServiceOptions>({
    where: { role: { $eq: "foreman" }, regionId },
  });

  useEffect(() => {
    if (isOpen) {
      dispatch(getAllEmployeesWithImage(query, true));
    }
  }, [dispatch, query, isOpen]);

  const handleClickClose = () => {
    dispatch(setEmployeesReqStatus(ReqStatus.IDLE));
    setForemanIdSelected("");
    onClickCancel();
  };

  const handleClickCheckbox = (idRow: string, isChecked: boolean) => {
    if (isChecked) {
      setForemanIdSelected(idRow);
    }

    if (!isChecked) {
      setForemanIdSelected("");
    }
  };

  const handleChangeSearch = useCallback(
    (employeeId: string, employeeName: string) => {
      setSearchedName(employeeName);
      setSearchedEmployeeId(employeeId);
    },
    [],
  );

  const handleClickSave = () => {
    if (foremanIdSelected) {
      const foremanId = foremanIdSelected;
      dispatch(setEmployeesReqStatus(ReqStatus.IDLE));
      setForemanIdSelected("");
      onClickCancel();
      onClickSave(foremanId);
    }
  };

  const filterEmployeesBySearchResult = (data: EmployeeModel[]) => {
    if (searchedEmployeeId && searchedName) {
      return data.filter(
        ({ employeeId, firstName }) =>
          employeeId === searchedEmployeeId && firstName === searchedName,
      );
    }
    return data;
  };

  const getForemanImg = () => {
    if (!foremanAssigned) return "";
    if (!foremanAssigned.employeePictures) return "";
    if (foremanAssigned.employeePictures.length <= 0) return "";

    return foremanAssigned.employeePictures[0].picture.url;
  };

  return (
    <GlobalModal
      open={isOpen}
      title="Assign Foreman"
      onClickContinue={handleClickSave}
      onClickClose={handleClickClose}
    >
      <Box padding="0px 24px">
        <Typography variant="h6" color="primary">{`Assigned (${
          foremanAssigned ? 1 : 0
        })`}</Typography>
        {foremanAssigned ? (
          <Box padding="24px" display="flex" alignItems="center">
            <Avatar src={getForemanImg()} />
            <Box margin="0 32px">
              <Typography variant="body2" className={classes.textAssigned}>
                {`${foremanAssigned.firstName} ${foremanAssigned.lastName}`}
              </Typography>
            </Box>
            <Typography variant="body2" className={classes.textAssigned}>
              {foremanAssigned.employeeId}
            </Typography>
          </Box>
        ) : (
          <Box padding="24px" display="flex" justifyContent="center">
            <Typography>Choose an available foreman</Typography>
          </Box>
        )}
        <Divider />
        <Box padding="24px 0">
          <Typography variant="h6" color="primary">{`Available (${
            isLoading ? "0" : foremenList.length
          })`}</Typography>
        </Box>
        <Box marginBottom="24px">
          <SearchByNameOrId
            filteredEmployees={foremenList}
            onChange={handleChangeSearch}
          />
        </Box>

        <TableCustom
          columns={supervisorAssignmentColumns}
          isLoading={isLoading}
          noRowsMessage="No foremen found"
          rows={getForemanAssignmentRows(
            filterEmployeesBySearchResult(foremenList),
            foremanIdSelected,
            handleClickCheckbox,
          )}
          onClickSort={noop}
        />
      </Box>
    </GlobalModal>
  );
}
